import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cls } from '@/shared/lib/classes';

/**
 * Botão.
 *
 * Altura mínima de 48px em todas as variantes: a mesma tela é usada em obra,
 * com luva (.spec/014, seção 6). A sombra deslocada e o canto quase reto vêm do
 * sistema de prancheta — papel sobre papel, não cartão flutuante.
 *
 * ## Por que o primário é verde, e não laranja
 *
 * O kit da marca traz os dois: `.onl-btn-primario` no verde nível e
 * `.onl-btn-destaque` no laranja prumo. Aqui só o verde virou variante. O
 * laranja é o acento — ele já marca foco, cota, seleção e o quadrado das
 * iniciais —, e um botão laranja em toda tela de cadastro faria o acento virar
 * o fundo: quando tudo é destaque, nada é. Se um dia aparecer a tela com **uma**
 * ação acima das outras (a que fecha a compra, a que confirma a nota), o
 * `destaque` entra como quinta variante, e não como o novo padrão.
 */

export type VarianteBotao = 'primario' | 'secundario' | 'fantasma' | 'perigo';
export type TamanhoBotao = 'normal' | 'compacto' | 'campo';

const BASE =
  'inline-flex items-center justify-center gap-2 border font-medium transition-[transform,background-color,box-shadow] duration-100 disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none active:translate-x-[1px] active:translate-y-[1px] active:shadow-none';

const VARIANTES: Record<VarianteBotao, string> = {
  primario:
    'border-verdete-500 bg-verdete-500 text-papel-50 shadow-prancheta hover:border-verdete-700 hover:bg-verdete-700',
  secundario:
    'border-grafite-300 bg-papel-50 text-grafite-900 shadow-prancheta hover:border-grafite-900 hover:bg-papel-100',
  fantasma: 'border-transparent bg-transparent text-grafite-700 hover:bg-papel-200',
  perigo: 'border-perigo-500 bg-perigo-500 text-white shadow-prancheta hover:bg-perigo-700',
};

const TAMANHOS: Record<TamanhoBotao, string> = {
  normal: 'min-h-toque px-5 text-sm',
  compacto: 'min-h-[38px] px-3 text-[13px]',
  /*
   * `campo` é o botão que mora **na linha de um campo** — o "Buscar" do CNPJ e
   * do CEP. Ele acompanha a altura do controle ao lado (`min-h-controle`), e é
   * a única razão de existir de um terceiro tamanho: com `compacto` ele ficava
   * 10px mais baixo do que o campo, e a linha nascia torta; com `normal` ele
   * deixaria de encolher junto no escopo denso e a torta voltaria ao contrário.
   */
  campo: 'min-h-controle px-3 text-[13px]',
};

export interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao;
  tamanho?: TamanhoBotao;
  carregando?: boolean;
  children?: ReactNode;
}

/** Marcado como estático fora do componente: não muda entre renderizações. */
const GIRANDO = (
  <svg
    className="h-4 w-4 animate-spin"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2" />
    <path d="M14.5 8A6.5 6.5 0 0 0 8 1.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export const Botao = forwardRef<HTMLButtonElement, BotaoProps>(function Botao(
  {
    variante = 'secundario',
    tamanho = 'normal',
    carregando = false,
    disabled,
    children,
    className,
    ...resto
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled === true || carregando}
      aria-busy={carregando}
      className={cls(BASE, VARIANTES[variante], TAMANHOS[tamanho], className)}
      {...resto}
    >
      {carregando ? GIRANDO : null}
      {children}
    </button>
  );
});
